import { ChangeEvent, FC, Fragment, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Typography } from '@mui/material';

import { ReactComponent as UploadIcon } from '@/assets/icons/import-icon.svg';
import AppButton from '@/shared/ui/AppButton';

import OperationSectionList, { OperationSection } from '../ImagesModal/OperationSectionList';
import FileCard, { FileData } from './FileCard';
import {
  CloseButtonContainer,
  CloseIconButton,
  ErrorBanner,
  FileListContainer,
  RightSideContainer,
  SidebarHeader,
  UploadContainer,
} from './ImageSlider.styles';

interface SimpleImageSidebarProps {
  files: FileData[];
  operationSections?: OperationSection<FileData>[];
  initialExpandedOperationId?: string;
  selectedFileId: number | null;
  errorMessage: string | null;
  fileInputRef: RefObject<HTMLInputElement | null>;
  title?: string;
  onClose: () => void;
  onUploadClick: () => void;
  onUploadToOperation?: (operationId?: string) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSelectFile: (id: number) => void;
  onDeleteFile: (id: number) => void;
  hasCloseButton?: boolean;
  hasDeleteOption?: boolean;
  hasUploadOption?: boolean;
}

const ImagePdfSidebar: FC<SimpleImageSidebarProps> = ({
  files,
  operationSections,
  initialExpandedOperationId,
  selectedFileId,
  errorMessage,
  fileInputRef,
  title,
  onClose,
  onUploadClick,
  onUploadToOperation,
  onFileChange,
  onSelectFile,
  onDeleteFile,
  hasCloseButton = true,
  hasDeleteOption = true,
  hasUploadOption = true,
}) => {
  const { t } = useTranslation();

  const renderFileCard = (file: FileData) => (
    <FileCard
      file={file}
      active={selectedFileId === file.id}
      onSelect={onSelectFile}
      onDelete={onDeleteFile}
      hasDeleteOption={hasDeleteOption}
    />
  );

  return (
    <RightSideContainer>
      <SidebarHeader>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, ml: 2 }}>
          {title || ''}
        </Typography>
        {hasCloseButton && (
          <CloseButtonContainer>
            <CloseIconButton onClick={onClose} sx={{ color: 'black' }}>
              <CloseIcon />
            </CloseIconButton>
          </CloseButtonContainer>
        )}
      </SidebarHeader>

      {errorMessage && (
        <ErrorBanner>
          <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: '1.2rem' }} />
          <Typography sx={{ color: '#B91C1C', fontSize: '0.75rem' }}>{errorMessage}</Typography>
        </ErrorBanner>
      )}
      {hasUploadOption && !operationSections && (
        <UploadContainer sx={{ mt: errorMessage ? 1 : 0 }}>
          <AppButton
            variant="primary"
            size="small"
            startIcon={<UploadIcon />}
            onClick={onUploadClick}
          >
            Upload files
          </AppButton>
        </UploadContainer>
      )}

      {hasUploadOption && (
        <input
          data-testid="simulation-modal-file-input"
          type="file"
          multiple
          accept="image/*,application/pdf"
          // @ts-ignore
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
      )}

      <FileListContainer>
        {operationSections ? (
          <OperationSectionList
            sections={operationSections}
            initialExpandedOperationId={initialExpandedOperationId}
            hasUploadOption={hasUploadOption && !!onUploadToOperation}
            uploadLabel={t('operation_group_add_files') as string}
            emptyText={t('operation_group_no_files_uploaded') as string}
            onUploadToOperation={onUploadToOperation}
            renderFile={renderFileCard}
          />
        ) : files.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: '#9CA3AF', mt: 4, fontSize: '0.875rem' }}>
            No files uploaded.
          </Typography>
        ) : (
          files.map((file) => <Fragment key={file.id}>{renderFileCard(file)}</Fragment>)
        )}
      </FileListContainer>
    </RightSideContainer>
  );
};

export default ImagePdfSidebar;
